import { useEffect } from "react";
import { getSpecialPriceApi, SpecialPriceRequest } from "features/car_model/api/carModelApi";
import { WheelTireCombination } from "features/mst_proc_fee/types/proc-fee";

/**
 * Custom hook to fetch special prices for wheel-tire combinations
 * @param wheelTireCombinations - The wheel-tire combinations to fetch prices for
 * @param onPriceUpdate - Callback to update prices
 */
const useSpecialPrices = (
  wheelTireCombinations: WheelTireCombination[],
  onPriceUpdate: (
    combinationIndex: number,
    tireIndex: number,
    price_tire: number,
    price_wheel: number,
  ) => void,
) => {
  useEffect(() => {
    const fetchSpecialPrices = async () => {
      if (wheelTireCombinations.length === 0) return;

      // Get store_cd from localStorage
      const store_cd = localStorage.getItem("shop_cd");
      if (!store_cd) return;

      // Build all API requests
      const requests: Array<{
        request: SpecialPriceRequest;
        combinationIndex: number;
        tireIndex: number;
      }> = [];

      wheelTireCombinations.forEach((combination, combIndex) => {
        combination.tires.forEach((tire, tireIndex) => {
          // Only fetch if quantity is 4
          if (tire.quantity === 4) {
            requests.push({
              request: {
                tire_item_cd: tire.tire_item_cd,
                wheel_item_id: combination.wheel.item_cd,
                store_cd,
              },
              combinationIndex: combIndex,
              tireIndex,
            });
          }
        });
      });

      // Call APIs in parallel to avoid repeated spinner toggles
      await Promise.all(
        requests.map(async ({ request, combinationIndex, tireIndex }) => {
          try {
            const response = await getSpecialPriceApi(request);

            // Update if both prices are greater than 0
            if (response && response.price_tire > 0 && response.price_wheel > 0) {
              onPriceUpdate(combinationIndex, tireIndex, response.price_tire, response.price_wheel);
            }
          } catch (error) {
            // Silently handle error
          }
        }),
      );
    };

    fetchSpecialPrices();
  }, [wheelTireCombinations.length, onPriceUpdate]);
};

export { useSpecialPrices };
export default useSpecialPrices;
